import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchRfqLifecycles, type LifecycleDto } from '../../lib/rfqLifecycleApi';
import type { LifecycleInput } from './lifecycleModel';

type Status = 'idle' | 'loading' | 'ready' | 'error';

export interface RfqLifecycles {
  status: Status;
  /** What to draw for this RFQ: its documents, or why there are none yet. Never a guess. */
  forRfq: (rfqId: string) => LifecycleInput;
  retry: () => void;
}

/**
 * The lifecycle of every RFQ on the board, fetched once per load of the board.
 *
 * Refetches when the board loads again (`loadKey` changes: a Refresh returns the same ids, and that is
 * exactly when the documents behind them may have moved on) or its RFQs change. Filtering and sorting
 * touch neither, so typing in the search box never refetches.
 *
 * `enabled` is false while the other view is showing, so the Board view costs nothing extra.
 */
export function useRfqLifecycles(rfqs: ReadonlyArray<{ _id: string }>, enabled: boolean, loadKey: number): RfqLifecycles {
  const [status, setStatus] = useState<Status>('idle');
  const [map, setMap] = useState<Record<string, LifecycleDto>>({});
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    if (!rfqs.length) {
      setMap({});
      setStatus('ready');
      return;
    }
    const controller = new AbortController();
    setStatus('loading');
    fetchRfqLifecycles(
      rfqs.map((r) => r._id),
      { signal: controller.signal }
    )
      .then((next) => {
        if (controller.signal.aborted) return;
        setMap(next);
        setStatus('ready');
      })
      .catch((err: any) => {
        if (controller.signal.aborted || err?.name === 'AbortError') return;
        setMap({});
        setStatus('error');
      });
    return () => controller.abort();
  }, [rfqs, enabled, attempt, loadKey]);

  const forRfq = useCallback(
    (rfqId: string): LifecycleInput => {
      if (status === 'idle' || status === 'loading') return 'loading';
      if (status === 'error') return 'unavailable';
      // Ready but the server did not return this RFQ: it is not "empty", it is unknown.
      return map[rfqId] || 'unavailable';
    },
    [status, map]
  );

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  // One stable object per change, so the memos downstream of it do not rerun on every render.
  return useMemo(() => ({ status, forRfq, retry }), [status, forRfq, retry]);
}
